import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Holds dynamic labels for CP BP admin breadcrumbs (names from API) and
 * parent/child context when opening a child BP from the Relationships tab.
 */
@Injectable({ providedIn: 'root' })
export class CpBpBreadcrumbContextService {
  /** Emitted when a feature screen updates BP name so breadcrumbs can re-render. */
  readonly refresh$ = new Subject<void>();

  /** Route param id of the BP whose name is {@link currentBpName}. */
  currentPortalId: string | null = null;
  currentBpName: string | null = null;

  /**
   * Parent + child ids when the user opened this child's customer-details from
   * Relationships; kept for that child's details, contact-list/:childId, and
   * relationship-list/:childId (survives breadcrumb refresh after API load).
   */
  private relationshipDetailTrail: {
    parentPortalId: string;
    parentName: string;
    childPortalId: string;
  } | null = null;

  setCurrentBp(portalId: string, name: string): void {
    this.currentPortalId = portalId;
    this.currentBpName = name || null;
    this.refresh$.next();
  }

  clearCurrentBp(): void {
    this.currentPortalId = null;
    this.currentBpName = null;
  }

  setRelationshipDetailTrail(
    parentPortalId: string,
    parentName: string,
    childPortalId: string,
  ): void {
    const anchor = this.normalizePortalId(parentPortalId);
    const opened = this.normalizePortalId(childPortalId);
    if (!anchor || !opened) {
      return;
    }
    this.relationshipDetailTrail = {
      parentPortalId: anchor,
      parentName: (parentName || anchor).trim(),
      childPortalId: opened,
    };
  }

  clearRelationshipDetailTrail(): void {
    this.relationshipDetailTrail = null;
  }

  getRelationshipDetailTrail(): {
    parentPortalId: string;
    parentName: string;
    childPortalId: string;
  } | null {
    return this.relationshipDetailTrail;
  }

  /**
   * Clears {@link relationshipDetailTrail} when leaving the child BP flow (details,
   * that child's contacts, or that child's relationships).
   */
  invalidateRelationshipTrailIfNeeded(activeLink: string): void {
    const trail = this.relationshipDetailTrail;
    if (!trail) {
      return;
    }
    const openedId = trail.childPortalId;
    const anchorId = trail.parentPortalId;

    const detailId = this.portalIdAfterSegment(activeLink, '/cp-customer/customer-details/');
    if (detailId != null && this.idsEqual(detailId, openedId)) {
      return;
    }
    const contactBpId = this.portalIdAfterSegment(activeLink, '/contact-list/');
    if (contactBpId != null && this.idsEqual(contactBpId, openedId)) {
      return;
    }
    const relId = this.portalIdAfterSegment(activeLink, '/cp-customer/relationship-list/');
    if (relId != null && this.idsEqual(relId, openedId)) {
      return;
    }
    if (relId != null && this.idsEqual(relId, anchorId)) {
      return;
    }
    this.relationshipDetailTrail = null;
  }

  private normalizePortalId(id: string | number | null | undefined): string | null {
    if (id == null) {
      return null;
    }
    const s = String(id).trim();
    return s !== '' ? s : null;
  }

  private idsEqual(a: string | null, b: string | null): boolean {
    if (a == null || b == null) {
      return false;
    }
    return String(a).trim() === String(b).trim();
  }

  private portalIdAfterSegment(activeLink: string, marker: string): string | null {
    const idx = activeLink.indexOf(marker);
    if (idx === -1) {
      return null;
    }
    const tail = activeLink.slice(idx + marker.length).split('?')[0];
    const seg = tail.split('/').filter(Boolean)[0];
    return seg != null && seg !== '' ? seg : null;
  }
}
